import type {
  SavingsDeposit,
  SavingsDepositAccrual,
  SavingsDepositAccrualDateRange,
  SavingsAccountBalance,
  SavingsDepositInterest,
  SavingsDepositInstructionPayload,
  SavingsDepositPayload,
  SavingsInterestPayment,
  SavingsInterestPaymentPayload
} from '@/interfaces/SavingsDeposit.interface';
import type {
  SavingsPanelDepositPayload,
  SavingsPanelDepositProducts,
  SavingsPanelDepositProductsParams
} from '@/interfaces/SavingsPanelDeposit.interface';
import type {
  SavingsTrackedDepositMarketProducts,
  SavingsTrackedDepositMarketProductsParams,
  SavingsTrackedDepositPayload
} from '@/interfaces/SavingsTrackedDeposit.interface';
import {
  SAVINGS_API_VERSION_HEADERS,
  SavingsReferenceResourceClient
} from '../savings-reference-resource';

export class ApiSavingsDeposits extends SavingsReferenceResourceClient<
  SavingsDeposit,
  SavingsDepositPayload
> {
  protected readonly path = '/savings-deposits';
  /** The endpoint has no free-text search; the list page leaves its search box off. */
  protected readonly searchFilter = 'bank_reference_number';

  async get(id: number): Promise<SavingsDeposit> {
    const res = await this.client.get(`${this.path}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async balance(id: number): Promise<SavingsAccountBalance> {
    const res = await this.client.get(`${this.path}/${id}/balance`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async payInterest(
    id: number,
    payload: SavingsInterestPaymentPayload
  ): Promise<SavingsInterestPayment> {
    const res = await this.client.post(
      `${this.path}/${id}/interest-payments`,
      payload,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  async getInterest(id: number): Promise<SavingsDepositInterest> {
    const res = await this.client.get(`${this.path}/${id}/interest`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async getSummaryPdf(id: number): Promise<Blob> {
    const res = await this.client.get(`${this.path}/${id}/summary-pdf`, {
      responseType: 'blob',
      headers: { ...SAVINGS_API_VERSION_HEADERS, Accept: 'application/pdf' }
    });
    return new Blob([res.data], { type: 'application/pdf' });
  }

  /** Raises an Instructions panel action; `instruct` instructs a saved Draft. */
  async raiseInstruction(
    id: number,
    payload: SavingsDepositInstructionPayload
  ): Promise<SavingsDeposit> {
    const res = await this.client.post(`${this.path}/${id}/instructions`, payload, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async listPanelProducts(
    customerId: number,
    params: SavingsPanelDepositProductsParams
  ): Promise<SavingsPanelDepositProducts> {
    const res = await this.client.get(
      `/savings-customers/${customerId}/panel-deposit-products`,
      { headers: SAVINGS_API_VERSION_HEADERS, params }
    );
    return res.data.data;
  }

  async createPanel(
    customerId: number,
    payload: SavingsPanelDepositPayload
  ): Promise<SavingsDeposit> {
    const res = await this.client.post(
      `/savings-customers/${customerId}/panel-deposits`,
      payload,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  async listTrackedMarketProducts(
    customerId: number,
    params: SavingsTrackedDepositMarketProductsParams
  ): Promise<SavingsTrackedDepositMarketProducts> {
    const res = await this.client.get(
      `/savings-customers/${customerId}/tracked-deposit-market-products`,
      { headers: SAVINGS_API_VERSION_HEADERS, params }
    );
    return res.data.data;
  }

  async createTracked(
    customerId: number,
    payload: SavingsTrackedDepositPayload
  ): Promise<SavingsDeposit> {
    const res = await this.client.post(
      `/savings-customers/${customerId}/tracked-deposits`,
      payload,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  async listAccruals(
    id: number,
    range: SavingsDepositAccrualDateRange
  ): Promise<SavingsDepositAccrual[]> {
    const res = await this.client.get(`${this.path}/${id}/accruals`, {
      headers: SAVINGS_API_VERSION_HEADERS,
      params: {
        'filter[start_date]': range.start_date,
        'filter[end_date]': range.end_date
      }
    });
    return Array.isArray(res.data?.data) ? res.data.data : [];
  }
}

export { savingsDepositsQueryKeys } from './query-keys';
export {
  useSavingsDepositAccrualsQuery,
  useSavingsDepositInterestQuery,
  useSavingsDepositQuery,
  useSavingsDepositsListQuery,
  useSavingsPanelDepositProductsQuery,
  useSavingsTrackedDepositMarketProductsQuery
} from './use-savings-deposits-queries';
export {
  useSavingsDepositBalanceMutation,
  useSavingsDepositInstructionMutation,
  useSavingsDepositsDeleteMutation,
  useSavingsDepositsUpdateMutation,
  useSavingsInterestPaymentMutation,
  useSavingsPanelDepositCreateMutation,
  useSavingsTrackedDepositCreateMutation
} from './use-savings-deposits-mutations';
export type { SavingsDepositsUpdateVariables } from './use-savings-deposits-mutations';
